import { ProductCreate } from 'components/product/product-create/ProductCreate'
import { useEffect, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useFetch } from 'services/useFetch'
import { API_PRODUCT } from 'services/apiProduct'
import {
    constructOptionsSelect,
    constructSelectedValue,
    convertErrorMessage,
    decodeHTML,
    formatUnixDateMoment,
    getSectionError,
    getValueFromArray,
    regexSlug,
    uploadFileToS3,
} from 'utils/utils'
import moment from 'moment'
import { Alert } from 'avo-ui'
import { SUBMIT_TYPE } from 'constants/constant'
import { FIELD_NAME_DETAIL, GET_FIELD_NAME, INITIAL_STATE, SECTION_FIELDS } from 'configs/product/configFormProduct'

export const ProductCreateContainer = () => {
    const navigate = useNavigate()
    const [productDetail, setProductDetail] = useState(INITIAL_STATE)
    const [isLoadingUpload, setIsLoadingUpload] = useState(false)
    const [searchParams] = useSearchParams()
    const productId = searchParams.get('productId')
    const { fetching: getProductDetail, isLoading } = useFetch(API_PRODUCT.DETAIL)
    const { fetching: getUrlProductImages } = useFetch(API_PRODUCT.URL_PRODUCT_IMAGES)
    const { fetching: createProduct, isLoading: isCreating } = useFetch(API_PRODUCT.CREATE)
    const { fetching: updateProduct, isLoading: isUpdating } = useFetch(API_PRODUCT.UPDATE)
    const { fetching: draftProduct, isLoading: isDrafting } = useFetch(API_PRODUCT.DRAFT)
    const [errorForm, setErrorForm] = useState({})
    const sectionRef = {
        general_info: useRef(),
        product_data: useRef(),
        product_info: useRef(),
        product_page_setting: useRef(),
        seo_setting: useRef(),
    }

    const onChangeForm = (item) => {
        setProductDetail({ ...productDetail, ...item })
    }

    const setShowAlert = (message) => {
        return Alert({
            icon: 'success',
            title: 'Success',
            message,
        })
    }

    const setUploadProductImages = async (filesName, uploadDetail, type) => {
        let newMainImage = { ...productDetail?.main_image }
        const newOtherImage = [...productDetail?.other_images]

        try {
            const s3Urls = await getUrlProductImages({ files: filesName })
            const apiS3list = s3Urls?.data?.map(async (item, i) => {
                await uploadFileToS3(item, uploadDetail[i]?.file)
                if (uploadDetail[i]?.type === 'other_images') {
                    newOtherImage[uploadDetail[i]?.index] = {
                        ...newOtherImage[uploadDetail[i]?.index],
                        url: `${item?.url}${item?.fields?.key}`,
                    }
                } else {
                    newMainImage = { ...newMainImage, url: `${item?.url}${item?.fields?.key}` }
                }
            })
            await Promise.all(apiS3list)
            const newDetail = {
                ...productDetail,
                main_image: newMainImage,
                other_images: newOtherImage,
            }
            onSave(type, newDetail)
        } catch (err) {
            console.log('upload failed')
        }

        setIsLoadingUpload(false)
    }

    const validateForm = (type) => {
        return new Promise((resolve, reject) => {
            const isInvalidTime = moment(productDetail?.sale_end)?.isSameOrBefore(moment(productDetail?.sale_start))
            const errors = {}
            if (type === SUBMIT_TYPE?.PUBLISH) {
                Object.keys(productDetail).forEach((key) => {
                    if (GET_FIELD_NAME[key] && (!productDetail[key]?.toString() || productDetail[key]?.length === 0)) {
                        errors[key] = `${GET_FIELD_NAME[key]} wajib diisi`
                    }
                })
            } else if (type === SUBMIT_TYPE?.DRAFT && !productDetail?.name) {
                errors['name'] = `${GET_FIELD_NAME['name']} wajib diisi`
            }

            if (!regexSlug.test(productDetail?.slug)) {
                errors['slug'] = 'Format slug tidak sesuai (contoh : miraculous-series)'
            }

            if (isInvalidTime) {
                errors['sale_end'] = 'Jam berakhir sale tidak boleh kurang atau sama dengan jam mulai sale'
            }
            setErrorForm(errors)
            if (!Object.keys(errors).length) resolve()
        })
    }

    const onSubmit = (type) => {
        validateForm(type).then(async () => {
            const brandImages = [productDetail?.main_image, ...productDetail?.other_images]
            const uploadDetail = []
            const filesName = []

            brandImages.map((item, index) => {
                if (item?.url?.name) {
                    filesName.push(item?.url?.name?.replace(/\s/g, ''))
                    uploadDetail.push({
                        file: item?.url?.file,
                        type: index === 0 ? 'main_image' : 'other_images',
                        index: index === 0 ? 0 : index - 1,
                    })
                }
            })

            if (filesName.length) {
                setIsLoadingUpload(true)
                await setUploadProductImages(filesName, uploadDetail, type)
            } else {
                onSave(type)
            }
        })
    }

    const onSave = (type, payload = { ...productDetail }) => {
        const newPayload = {
            ...payload,
            brand_id: payload?.brand_id?.value,
            category_ids: getValueFromArray(payload?.category_ids, 'value'),
            type_ids: getValueFromArray(payload?.type_ids, 'value'),
            sale_start: payload?.sale_start ? moment(payload?.sale_start).utc().format('YYYY-MM-DD HH:mm') : null,
            sale_end: payload?.sale_end ? moment(payload?.sale_end).utc().format('YYYY-MM-DD HH:mm') : null,
            limit_purchase: Number(payload?.limit_purchase),
            description: decodeHTML(payload?.description),
            how_to_use: decodeHTML(payload?.how_to_use),
            ingredients: decodeHTML(payload?.ingredients),
        }

        if (!productId && type === SUBMIT_TYPE?.PUBLISH) {
            createProduct(newPayload)
                .then(() => {
                    setShowAlert('Product Created !')
                    navigate('/product/product-list')
                })
                .catch((error) => setErrorForm(convertErrorMessage(error?.data, FIELD_NAME_DETAIL)))
        } else if (type === SUBMIT_TYPE?.PUBLISH) {
            updateProduct(newPayload, productId)
                .then(() => {
                    setShowAlert('Product Updated !')
                    navigate('/product/product-list')
                })
                .catch((error) => setErrorForm(convertErrorMessage(error?.data, FIELD_NAME_DETAIL)))
        } else {
            draftProduct(newPayload, productId)
                .then(() => {
                    setShowAlert('Product Drafted !')
                    navigate('/product/product-list')
                })
                .catch((error) => setErrorForm(convertErrorMessage(error?.data, FIELD_NAME_DETAIL)))
        }
    }

    useEffect(() => {
        const topSection = getSectionError(errorForm, SECTION_FIELDS)[0]
        sectionRef?.[topSection]?.current?.scrollIntoView({ behavior: 'smooth' })
    }, [errorForm])

    useEffect(() => {
        if (productId) {
            getProductDetail({}, productId).then((response) => {
                const { brand, categories, type, main_image, ...data } = { ...INITIAL_STATE, ...response?.data }
                const isMainImageExist = main_image?.url && main_image?.title
                const newDetail = {
                    ...data,
                    main_image: isMainImageExist ? main_image : null,
                    brand_id: constructSelectedValue(brand, 'name', 'id'),
                    category_ids: constructOptionsSelect(categories, 'name', 'id'),
                    type_ids: constructOptionsSelect(type, 'name', 'id'),
                    sale_start: data?.sale_start ? formatUnixDateMoment(data?.sale_start, 'YYYY-MM-DD HH:mm') : null,
                    sale_end: data?.sale_end ? formatUnixDateMoment(data?.sale_end, 'YYYY-MM-DD HH:mm') : null,
                    limit_purchase: data?.limit_purchase || null,
                }
                setProductDetail(newDetail)
            })
        } else {
            setProductDetail(INITIAL_STATE)
        }
        setErrorForm({})
        window.scrollTo({ top: 0, behavior: 'smooth' })
    }, [productId])

    const props = {
        sectionProps: {
            sectionRef,
            productDetail,
            onChangeForm,
            errorForm,
        },
        isLoading,
        isLoadingUpload,
        isCreating,
        isUpdating,
        isDrafting,
        onSubmit,
    }

    return <ProductCreate {...props} />
}
